import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { DealCalculationOverview } from '../../../../../generated/ApiClient';
import { Icons } from '../../../../../component/icons/Icons';
import { HandoverIcon } from '../HandoverIcon';
import { TakeoverAction, resolveTakeoverAction } from './resolveTakeoverAction';

export const TakeoverInFlightContext = React.createContext<number | undefined>(undefined);

export interface TakeoverIconCellProps {
  dco: DealCalculationOverview;
  isFleetDeal: boolean;
  canTakeover: boolean;
  canPrint: boolean;
  canViewLocked: boolean;
  amendedByDealId?: number;
  modelIsActive: boolean;
  readonly?: boolean;
  onRowClick: (dco: DealCalculationOverview) => (e: React.MouseEvent) => void;
  onPrintClick: (dco: DealCalculationOverview) => (e: React.MouseEvent) => void;
  onTakeoverClick: (dco: DealCalculationOverview, action: TakeoverAction) => (e: React.MouseEvent) => void;
}

export function TakeoverIconCell({
  dco,
  isFleetDeal,
  canTakeover,
  canPrint,
  canViewLocked,
  amendedByDealId,
  modelIsActive,
  readonly,
  onRowClick,
  onPrintClick,
  onTakeoverClick,
}: TakeoverIconCellProps) {
  const { t } = useTranslation();
  const inFlightId = React.useContext(TakeoverInFlightContext);
  const isInFlight = inFlightId === dco.fundingProductId;
  const action = resolveTakeoverAction(dco, isFleetDeal, canTakeover);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <IconButton onClick={onRowClick(dco)}>
        {readonly || dco.isFinancingCalculationReadonly || !modelIsActive
          ? Icons.open()
          : Icons.pencil()}
      </IconButton>
      {canPrint && !amendedByDealId && (!dco.isSalesAidLocked || canViewLocked) && (
        <IconButton onClick={onPrintClick(dco)}>{Icons.print()}</IconButton>
      )}
      {action.mode !== 'hidden' && (
        // tooltipKey is a valid TakeoverTooltipKey; the cast only satisfies react-i18next's overload resolution
        // (it rejects a union-typed key argument).
        <Tooltip title={action.mode === 'disabled' ? t(action.tooltipKey! as Parameters<typeof t>[0]) : ''}>
          <span>
            <IconButton
              disabled={action.mode === 'disabled' || isInFlight}
              onClick={action.mode !== 'disabled' ? onTakeoverClick(dco, action) : undefined}
            >
              {isInFlight ? (
                <CircularProgress size={22} />
              ) : (
                <HandoverIcon sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
